import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ═══════════════════════════════
   CREATE COUPON
═══════════════════════════════ */
export const createCoupon = createAsyncThunk(
  "coupon/create",
  async (data, thunkAPI) => {
    try {
      const res = await api.post("/api/coupons", data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to create coupon");
    }
  }
);

/* ═══════════════════════════════
   FETCH ALL COUPONS
═══════════════════════════════ */
export const fetchCoupons = createAsyncThunk(
  "coupon/fetchAll",
  async ({ page = 1, limit = 10 } = {}, thunkAPI) => {
    try {
      const res = await api.get(`/api/coupons?page=${page}&limit=${limit}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch coupons");
    }
  }
);

/* ═══════════════════════════════
   UPDATE COUPON
═══════════════════════════════ */
export const updateCoupon = createAsyncThunk(
  "coupon/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await api.put(`/api/coupons/${id}`, data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to update coupon");
    }
  }
);

/* ═══════════════════════════════
   TOGGLE STATUS
═══════════════════════════════ */
export const toggleCouponStatus = createAsyncThunk(
  "coupon/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/coupons/${id}/status`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to toggle status");
    }
  }
);

/* ═══════════════════════════════
   DELETE COUPON
═══════════════════════════════ */
export const deleteCoupon = createAsyncThunk(
  "coupon/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/coupons/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete coupon");
    }
  }
);

/* ═══════════════════════════════
   SLICE
═══════════════════════════════ */
const couponSlice = createSlice({
  name: "coupon",
  initialState: {
    list:       [],
    pagination: {},
    loading:    false,
    error:      null,
    success:    false,
  },
  reducers: {
    resetCouponStatus: (state) => {
      state.loading = false;
      state.error   = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      /* create */
      .addCase(createCoupon.pending,   (state)          => { state.loading = true;  state.error = null; })
      .addCase(createCoupon.fulfilled, (state, action)  => { state.loading = false; state.success = true; state.list.unshift(action.payload); })
      .addCase(createCoupon.rejected,  (state, action)  => { state.loading = false; state.error = action.payload; })

      /* fetch all */
      .addCase(fetchCoupons.pending,   (state)          => { state.loading = true; })
      .addCase(fetchCoupons.fulfilled, (state, action)  => { state.loading = false; state.list = action.payload.data; state.pagination = action.payload.pagination; })
      .addCase(fetchCoupons.rejected,  (state, action)  => { state.loading = false; state.error = action.payload; })

      /* update */
      .addCase(updateCoupon.pending,   (state)          => { state.loading = true;  state.error = null; })
      .addCase(updateCoupon.fulfilled, (state, action)  => {
        state.loading = false; state.success = true;
        const idx = state.list.findIndex((c) => c._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
      })
      .addCase(updateCoupon.rejected,  (state, action)  => { state.loading = false; state.error = action.payload; })

      /* toggle status */
      .addCase(toggleCouponStatus.fulfilled, (state, action) => {
        const idx = state.list.findIndex((c) => c._id === action.payload._id);
        if (idx !== -1) state.list[idx].status = action.payload.status;
      })

      /* delete */
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.list = state.list.filter((c) => c._id !== action.payload);
      });
  },
});

export const { resetCouponStatus } = couponSlice.actions;
export default couponSlice.reducer;